import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/free-mode';
import { FreeMode } from 'swiper/modules';

const Rovovision = () => {
  return (
    <div id="main-wrapper">
      <Header />

      <div id="content" role="main">

        {/* Hero Section */}
        <section
          className="hero-wrap"
          style={{
            position: 'relative',
            overflow: 'hidden',
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            backgroundImage: "url('/images/projects/rovovision.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="container" style={{ position: 'relative', zIndex: 2, paddingTop: '70px' }}>
            <div className="row">
              <div className="col-12 text-center text-white">
                <h1 className="text-16 font-weight-600 mb-3 text-white">RovoVision</h1>
                <p className="lead text-white-100 mb-0">
                  Smart Industrial Safety & Monitoring Platform.
                </p>
              </div>
            </div>
          </div>
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', zIndex: 1 }}></div>
        </section>

        {/* Blog Intro */}
        <section className="py-5 bg-white text-dark">
          <div className="container">
            <div className="row align-items-center mb-5 g-4 flex-column-reverse flex-md-row">
              <div className="col-md-6 mb-4 mb-md-0">
                <img 
                  src="/images/projects/rovovision.png" 
                  alt="Robots on Factory Floor" 
                  className="img-fluid rounded-4 shadow-lg"
                  style={{ height: '350px', objectFit: 'cover', width: '100%' }}
                />
              </div>
              <div className="col-md-6">
                <h2 className="fw-bold mb-3" style={{ fontSize: "1.50rem" }}>
                  Real-Time Factory Safety Monitoring. Automated vehicle speed control, PPE compliance, and emergency headcount.
                </h2>
                <p className="text-black" style={{ fontSize: "1rem" }}>
                  Rovovision is an AI-powered safety and monitoring platform designed for industrial environments. It addresses critical on-site safety challenges such as vehicle over-speeding, PPE non-compliance, and emergency headcount management. By replacing manual monitoring with automated, real-time systems, Rovovision improves safety enforcement, visibility, and response across factory premises.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What is RovoVision */}
        <section className="py-5 bg-light text-dark">
          <div className="container">
            <div className="row mb-4">
              <div className="col-lg-10 mx-auto text-center">
                <h2 className="fw-bold mb-3">What is RovoVision?</h2>
                <p className="text-black" style={{ fontSize: "1rem" }}>
                  Rovovision is a computer-vision-based solution suite that uses high-resolution cameras, GPUs, and intelligent analytics to monitor activities within factory premises. It continuously tracks vehicles, workers, and people movement to detect safety violations, verify compliance, and provide real-time alerts and actionable insights through centralized dashboards.
                </p>
              </div>
            </div>

            <div className="row g-4">
              {[
                { title: "SafeTrack Auto – Vehicle Speed Monitoring", text: "Automatically monitors vehicles inside factory premises by calculating live speed against predefined safety limits. Detects overspeeding in real time, captures violation evidence, and ensures consistent enforcement without manual checks.", icon: "bi bi-display", img: "/images/projects/safe.png" },
                { title: "StepSafe – Safety Shoe Compliance Monitoring", text: "Ensures workers wear certified safety shoes, verifies compliance, and sends instant alerts for non-compliance while allowing approved workers.", icon: "bi bi-activity", img: "/images/projects/steps.jpeg" },
                { title: "SafeCount Pro – Emergency Headcount & Evacuation Tracking", text: "Tracks employees and visitors inside premises and counts people reaching assembly points during emergencies using cameras, facial recognition, RFID, and BLE.", icon: "bi bi-robot", img: "/images/projects/count.jpeg" },
                { title: "Centralized Safety Dashboard & Alerts", text: "Unified dashboard showing live vehicle movement, worker compliance, visitor presence, and emergency status. Sends instant alerts and maintains time-stamped records for audits.", icon: "bi bi-clock", img: "/images/projects/cen.jpeg" },
              ].map((feature, idx) => (
                <div className="col-12 col-md-6 col-lg-6 mb-4" key={idx}>
                  <div className="card h-100 border-0 shadow-lg rounded-4 overflow-hidden">
                    <img 
                      src={feature.img} 
                      alt={feature.title} 
                      className="card-img-top"
                      style={{ height: '250px', objectFit: 'cover', width: '100%' }} 
                    />
                    <div className="card-body">
                      <div className="mb-2 text-primary">
                        <i className={`${feature.icon} fs-2`}></i>
                      </div>
                      <h5 className="fw-bold mb-3">{feature.title}</h5>
                      <p className="text-muted mb-0">{feature.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Business Impact */}
        <section className="py-5 bg-light text-dark">
          <div className="container">
            <div className="row align-items-center g-4 flex-column-reverse flex-md-row">
              <div className="col-md-6">
                <h2 className="fw-bold mb-4">Business Impact</h2>
                <p className="text-black" style={{ fontSize: "1rem" }}>
                  Rovovision significantly reduces safety risks and operational gaps caused by manual monitoring. It helps prevent accidents, improves compliance enforcement, and ensures faster response during emergencies. By automating detection and reporting, organizations gain consistent safety oversight, reduced dependency on human judgment, and improved accountability for employees and visitors.
                </p>
              </div>
              <div className="col-md-6">
                <img
                  src="https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?auto=format&fit=crop&w=800&q=80"
                  alt="Business Impact"
                  className="img-fluid rounded-4 shadow-lg"
                  style={{ height: '400px', objectFit: 'cover', width: '100%' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Key Features & Capabilities */}
        <section className="py-5 bg-white text-dark">
          <div className="container">
            <div className="row mb-4">
              <div className="col-lg-10 mx-auto text-center">
                <h2 className="fw-bold mb-3">Key Features & Capabilities</h2>
                <p className="text-muted mb-5">
                  SafeTrack Auto delivers real-time vehicle speed monitoring, instant alerts, and actionable safety intelligence across factory premises.
                </p>
              </div>
            </div>

            {/* Horizontal scrollable cards */}
            <div
              className="d-flex flex-row flex-nowrap"
              style={{ gap: "1rem", paddingBottom: "1rem", overflowX: "auto", scrollbarWidth: "none", msOverflowStyle: "none" }}
              onWheel={(e) => { e.currentTarget.scrollLeft += e.deltaY; }}
            >
              <style>
                {`
                  div::-webkit-scrollbar { display: none; }
                  @media (max-width: 768px) {
                    .card { min-width: 250px !important; height: auto !important; }
                    .card img { height: 180px !important; }
                  }
                `}
              </style>

              {[
                { title: "SafeTrack Auto – Vehicle Speed Monitoring", text: "Detects and tracks vehicles, calculates speed, identifies violations, captures evidence, and sends instant alerts.", icon: "bi bi-collection", img: "/images/projects/safe.png" },
                { title: "Real-Time AI Surveillance", text: "24/7 automated monitoring using cameras and GPU-based analytics.", icon: "bi bi-speedometer2", img: "/images/projects/real.png" },
                { title: "Instant Alert & Notification System", text: "Sends real-time email/system alerts for safety rule violations.", icon: "bi bi-bar-chart", img: "/images/projects/alrt.png" },
                { title: "Violation Evidence Management", text: "Records violation data including image, plate, speed, location, and timestamp for audits.", icon: "bi bi-controller", img: "/images/projects/vi.png" },
                { title: "Centralized Safety Dashboard", text: "Displays all entries, violations, limits, and trends in an interactive dashboard.", icon: "bi bi-layers", img: "/images/projects/ce.png" },
                { title: "Configurable Safety Zones & Rules", text: "Allows admins to define speed limits for different gates, paths, and roads.", icon: "bi bi-layers", img: "/images/projects/c.png" },
                { title: "Data Logging & Compliance Reporting", text: "Maintains a digital history of vehicle movement and violations for analysis and compliance.", icon: "bi bi-layers", img: "/images/projects/datal.png" },
              ].map((feature, idx) => (
                <div key={idx} className="card flex-shrink-0 border-0 shadow-lg rounded-4 overflow-hidden" style={{ width: "300px" }}>
                  <img src={feature.img} alt={feature.title} className="card-img-top" style={{ height: "200px", objectFit: "cover" }} />
                  <div className="card-body">
                    <div className="mb-2 text-primary"><i className={`${feature.icon} fs-2`}></i></div>
                    <h5 className="fw-bold mb-2">{feature.title}</h5>
                    <p className="text-muted mb-0">{feature.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose RovoVision */}
        <section className="py-5 bg-light text-dark">
          <div className="container">
            <div className="row align-items-center g-4 flex-column-reverse flex-md-row">
              <div className="col-md-6">
                <img
                  src="/images/projects/why2.png"
                  alt="Why Choose RovoVision"
                  className="img-fluid rounded-4 shadow-lg mb-4"
                  style={{ height: '400px', objectFit: 'cover', width: '100%' }}
                />
              </div>
              <div className="col-md-6">
                <h2 className="fw-bold mb-4">Why Choose RovoVision?</h2>
                <p className="text-muted mb-4">
                  Rovovision eliminates manual safety check limitations by providing continuous, automated monitoring with real-time visibility, accurate enforcement, and reliable data during operations and emergencies.
                </p>
                <ul className="list-unstyled text-muted fs-6">
                  <li className="mb-3"><strong>Real-Time Decision Making:</strong> Instant alerts enable faster response to safety violations.</li>
                  <li className="mb-3"><strong>Centralized Safety Intelligence:</strong> Single dashboard for vehicles, people, and compliance status.</li>
                  <li className="mb-3"><strong>Captures verified visual evidence:</strong> For every violation and incident.</li>
                  <li className="mb-3"><strong>Supports emergency response:</strong> Live headcount and assembly-point tracking.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Conclusion & CTA */}
        <section className="py-5 bg-dark text-white text-center">
          <div className="container">
            <p className="lead mb-4 mx-auto" style={{ maxWidth: '800px' }}>
              Rovovision transforms industrial safety management by combining AI vision, real-time analytics, and automated reporting into a single platform. From preventing vehicle accidents and enforcing PPE compliance to ensuring accurate emergency evacuation tracking, Rovovision delivers proactive safety, faster response, and complete operational visibility.
            </p>
            <a href="/contact" className="btn btn-primary btn-lg rounded-pill px-5">Inquire About Partnership</a>
          </div>
        </section>

      </div>

      <Footer />
    </div>
  );
};

export default Rovovision;
